import { useQuery } from "@apollo/client";

import Box from "ds/components/Box";
import Button from "ds/components/Button";
import PageLoading from "components/loading/PageLoading";
import PageInfoHeader from "ds/components/PageInfoHeader";
import PageWrapper from "components/PageWrapper";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageAuditTrail } from "hooks/useAnalytics/pages/auditTrail";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { AccountContext } from "views/AccountWrapper";

import { showAuditTrailDeleteConfirmation } from "./DeleteConfirmation";
import { showEditDrawer } from "./EditDrawer";
import Empty from "./Empty";
import { GET_AUDIT_TRAIL_WEBHOOK } from "./gql";
import View from "./View";
import styles from "./styles.module.css";

const AuditTrailConfig = () => {
  const { accountName } = useTypedContext(AccountContext);

  const { loading, data, error } = useQuery(GET_AUDIT_TRAIL_WEBHOOK);

  useTitle(`Audit trail · Configuration · ${accountName}`);

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageAuditTrail.Configuration,
  });

  const integration = data?.auditTrailWebhook;

  const onEditMode = () => {
    trackSegmentAnalyticsEvent("Edit clicked");
    showEditDrawer({ integration: integration || undefined });
  };

  const onDeleteClick = () => {
    trackSegmentAnalyticsEvent("Delete clicked");
    showAuditTrailDeleteConfirmation();
  };

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data) {
    return <PageLoading />;
  }

  const isViewMode = !!integration;

  const openCreateForm = () => {
    trackSegmentAnalyticsEvent("Set up clicked");
    showEditDrawer();
  };

  return (
    <>
      <PageInfoHeader title="Configuration">
        {isViewMode && integration && (
          <Box gap="large">
            <Button type="button" onPress={onEditMode} variant="primary">
              Edit
            </Button>

            <Button type="button" variant="dangerPrimary" onPress={onDeleteClick}>
              Delete
            </Button>
          </Box>
        )}
        {!integration && (
          <Button type="button" variant="primary" onPress={openCreateForm}>
            Set up
          </Button>
        )}
      </PageInfoHeader>
      <PageWrapper className={styles.wrapper} padding="0" margin="0">
        {integration ? <View {...integration} /> : <Empty />}
      </PageWrapper>
    </>
  );
};

export default AuditTrailConfig;
